import { useMemo } from 'react';
import { LineChart } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Chart } from '@vnetwork/ui/charts';
import type { UsageSeriesPoint } from '@/api/s3';
import { formatGiB } from '@/pages/services/oss/storage-format';

/** Short "Jun 29" axis/tooltip label from an ISO/epoch timestamp. */
function fmtDay(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Area chart of billed storage over the trailing ~30 days. `bytes` is the padded
 * billed figure Wasabi charges on, so it — not `raw_bytes` — is what's plotted.
 */
export function StorageUtilizationChart({
  series,
  isLoading,
  title = 'Storage utilization (30 days)',
  description = 'Daily billed storage across all buckets.',
}: {
  series: UsageSeriesPoint[];
  isLoading: boolean;
  /** Override the card heading — e.g. per-bucket views scope it to one bucket. */
  title?: string;
  description?: string;
}) {
  const data = useMemo(
    () => series.map((p) => ({ date: fmtDay(p.ts), bytes: Number(p.bytes) || 0 })),
    [series],
  );

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton variant="rect" height={260} />
        ) : data.length === 0 ? (
          <EmptyState
            size="sm"
            icon={<LineChart size={20} />}
            title="No utilization history yet"
            description="Storage history is only available for services that support usage reporting."
          />
        ) : (
          <Chart
            type="area"
            data={data}
            xKey="date"
            series={[{ key: 'bytes', label: 'Storage' }]}
            height={260}
            valueFormatter={(v) => formatGiB(Number(v) || 0)}
          />
        )}
      </CardBody>
    </Card>
  );
}
